<template>
  <div>
    <div class="card">
      <div class="card-body">
        <!--
          Store mode keeps its own order: the store scopes the equipment, and the store's opening
          slots can only be computed once a product is known. Every other mode starts from the
          period and then lists the equipment free during it.
        -->
        <template v-if="mode === 'store'">
          <BookingStepCard :label="$t('Store')">
            <StoreCard v-if="booking.store" :store="booking.store"></StoreCard>
            <BookingStepPlaceholder v-else :label="$t('No store selected')" :icon="Store" />
            <template #action>
              <BookingStepEditButton @click="showStoreModal = true" />
            </template>
          </BookingStepCard>

          <BookingStepCard v-if="selectedStore" :label="$t('Item')">
            <EquipmentInfo v-if="booking.equipment" :item="booking.equipment" />
            <BookingStepPlaceholder v-else :label="$t('No item selected')" :icon="Package" />
            <template #action>
              <BookingStepEditButton @click="showEquipmentModal = true" />
            </template>
          </BookingStepCard>

          <BookingStepCard v-if="booking.equipment" :label="$t('Dates')">
            <DateCard :mode="mode" :booking="booking"></DateCard>
            <template #action>
              <BookingStepEditButton @click="showDateModal = true" />
            </template>
          </BookingStepCard>
        </template>

        <template v-else>
          <BookingStepCard :label="$t('Dates')">
            <DateCard v-if="hasDates" :mode="mode" :booking="booking"></DateCard>
            <BookingStepPlaceholder v-else :label="$t('No dates selected')" :icon="CalendarDays" />
            <template #action>
              <BookingStepEditButton @click="showDateModal = true" />
            </template>
          </BookingStepCard>

          <BookingStepCard :label="$t('Item')">
            <EquipmentInfo v-if="booking.equipment" :item="booking.equipment" />
            <BookingStepPlaceholder v-else :label="$t('No item selected')" :icon="Package" />
            <Alert v-if="equipmentNeedsRecheck" variant="warning" class="tw:mt-3 tw:mb-0">
              {{ $t("The dates changed since this item was picked. Check it is still available.") }}
            </Alert>
            <template #action>
              <BookingStepEditButton @click="showEquipmentModal = true" />
            </template>
          </BookingStepCard>
        </template>

        <BookingStepCard
          v-if="booking.equipment && hasDates"
          :label="$t('Participant')"
          :with-separator="false"
        >
          <RegistrationInfo v-if="booking.registration" :registration="booking.registration" />
          <BookingStepPlaceholder v-else :label="$t('No participant selected')" :icon="CircleUserRound" />
          <template #action>
            <BookingStepEditButton @click="showRegistrationModal = true" />
          </template>
        </BookingStepCard>

        <div class="tw:mt-6">
          <button type="submit" class="btn btn-primary btn-lg">
            {{ $t("Create booking") }}
          </button>
        </div>
      </div>
    </div>

    <StoreDialog
      v-model:open="showStoreModal"
      :stores="stores"
      @select="onStoreSelected"
    />

    <EquipmentDialog
      v-model:open="showEquipmentModal"
      :base-filters="equipmentFilters"
      :available-filters="equipmentFilterDefinitions"
      :product-metafields="productMetafields"
      :equipment-metafields="equipmentMetafields"
      with-availability
      :timezone="timezone"
      @select="onEquipmentSelected"
    />

    <DateDialog
      v-model:open="showDateModal"
      :mode="mode"
      :booking="booking"
      :stages="stages"
      :timezone="timezone"
      :default-start-time="bookingStartTime"
      :default-end-time="bookingEndTime"
      :api-url="apiUrl"
      @stage-range="stageDateRange"
      @stage-store-slot="stageStoreSlot"
      @stage-slot="stageSlot"
      @discard="discardPendingDates"
      @save="onDatesSaved"
    />

    <RegistrationDialog
      v-model:open="showRegistrationModal"
      :base-filters="registrationBaseFilters"
      :available-filters="registrationAvailableFilters"
      :add-url="registrationAddUrl"
      @select="onRegistrationSelected"
    />

    <BookingHiddenInputs
      :mode="mode"
      :equipment-id="selectedEquipment"
      :bookable-id="bookableId"
      :date-range="selectedDateRange"
      :registration-id="selectedRegistration"
    />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { CalendarDays, CircleUserRound, Package, Store } from "@lucide/vue";
import BookingStepCard from "@/components/booking/BookingStepCard.vue";
import BookingStepEditButton from "@/components/booking/BookingStepEditButton.vue";
import BookingStepPlaceholder from "@/components/booking/BookingStepPlaceholder.vue";
import BookingHiddenInputs from "@/components/booking/BookingHiddenInputs.vue";
import StoreDialog from "@/components/booking/dialogs/StoreDialog.vue";
import EquipmentDialog from "@/components/booking/dialogs/EquipmentDialog.vue";
import DateDialog from "@/components/booking/dialogs/DateDialog.vue";
import RegistrationDialog from "@/components/booking/dialogs/RegistrationDialog.vue";
import DateCard from "@/components/booking/DateCard.vue";
import StoreCard from "@/components/store/StoreCard.vue";
import Alert from "@/components/ui/Alert.vue";
import RegistrationInfo from "@/components/registration/RegistrationInfo.vue";
import EquipmentInfo from "@/components/equipment/EquipmentInfo.vue";
import { useBookingDraft } from "@/composables/useBookingDraft";

const props = defineProps({
  mode: {
    type: String,
    required: true,
    validate: (value) => ["slot", "date", "store"].includes(value)
  },
  timezone: {
    type: String,
    required: true
  },
  bookingStartTime: {
    type: String,
    default: null
  },
  bookingEndTime: {
    type: String,
    default: null
  },
  stages: {
    type: Array,
    default: () => []
  },
  stores: {
    type: Array,
    default: () => []
  },
  equipmentBaseFilters: {
    type: Object,
    default: () => ({})
  },
  equipmentAvailableFilters: {
    type: Array,
    default: () => []
  },
  productMetafields: {
    type: Array,
    default: () => []
  },
  equipmentMetafields: {
    type: Array,
    default: () => []
  },
  registrationBaseFilters: {
    type: Object,
    default: () => ({})
  },
  registrationAvailableFilters: {
    type: Array,
    default: () => []
  },
  registrationAddUrl: {
    type: String,
    default: null
  },
  apiUrl: {
    type: String,
    required: true
  }
});

const {
  booking,
  selectedEquipment,
  selectedStore,
  selectedRegistration,
  selectedDateRange,
  bookableId,
  equipmentFilters,
  hasDates,
  equipmentNeedsRecheck,
  withAvailabilityDefault,
  setStore,
  setEquipment,
  setRegistration,
  stageDateRange,
  stageStoreSlot,
  stageSlot,
  commitDates,
  discardPendingDates
} = useBookingDraft({
  mode: props.mode,
  stores: props.stores,
  equipmentBaseFilters: props.equipmentBaseFilters
});

const isStoreMode = props.mode === "store";

// Store mode opens on the store, every other mode on the period.
const showStoreModal = ref(isStoreMode);
const showDateModal = ref(!isStoreMode);
const showEquipmentModal = ref(false);
const showRegistrationModal = ref(false);

// The equipment list opens already filtered on the chosen period; it stays an ordinary filter the
// user can widen or clear from the filter bar.
const equipmentFilterDefinitions = computed(
  () => withAvailabilityDefault(props.equipmentAvailableFilters)
);

// Each step hands over to the next one.
function onStoreSelected(storeId) {
  setStore(storeId);
  showStoreModal.value = false;
  showEquipmentModal.value = true;
}

function onEquipmentSelected(equipment) {
  setEquipment(equipment);
  showEquipmentModal.value = false;

  // Store mode picks the equipment before its dates; the others already have them.
  if (isStoreMode) {
    showDateModal.value = true;
  } else if (hasDates.value) {
    showRegistrationModal.value = true;
  }
}

function onDatesSaved() {
  commitDates();
  showDateModal.value = false;

  if (isStoreMode) {
    showRegistrationModal.value = true;
    return;
  }

  // Dates first: move on to the equipment now that the list can be narrowed to the period.
  if (!booking.value.equipment) {
    showEquipmentModal.value = true;
  }
}

function onRegistrationSelected(registration) {
  setRegistration(registration);
  showRegistrationModal.value = false;
}
</script>
